import { IconButton, Modal } from "@mui/material";
import "../../UserCollectedData.css";
import "./EditSearch.css";
import Box from "@mui/material/Box";
import Board from "@publicPortal/components/board/Board";
import EnforcementNoticeAdvancedSearch from "@publicPortal/components/advancedSearch/EnforcementNoticeAdvancedSearch";
import { EnforcementNoticeSearchState } from "@publicPortal/pages/search/EnforcementNotice/EnforcementNoticeSearchState";
import { EnforcementNoticeFilter } from "@shared/publicportal/contracts/EnforcementNoticeFilter";
import React, { lazy, Suspense, useMemo, useRef, useState } from "react";
import { EditSavedSearch } from "./PlanningApplicationEditSearch";
import EnforcementNoticeService from "@publicPortal/services/EnforcementNoticeService";
import PublicPortalState from "@publicPortal/utils/InitialState";
import { IsStringNotEmptyValidator, ValidationResult } from "@shared/mirage/validators/IsStringNotEmptyValidator";
import UserCollectedDataHelper from "../../UserCollectedDataHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { PublicPortalTheme } from "@publicPortal/theme/MuiPublicPortal.module";
import EnforcementNoticeSimpleSearch from "@publicPortal/pages/search/EnforcementNotice/EnforcementNoticeSimpleSearch";
import MapFallback from "@mirage/components/MapView/components/MapFallback";
import SearchActions from "@publicPortal/pages/search/SearchActions";
import { PPConstants } from "@shared/publicportal/constants";
import useSessionStorage from "@mirage/hooks/useSessionStorage";

const MapView = lazy(() =>
    import("@mirage/components/MapView/MapView").then((module) => {
        return { default: module.MapView };
    })
);

const EnforcementNoticeEditSearch: React.FC<EditSavedSearch> = (props) => {
    const enfNoticeService = new EnforcementNoticeService();
    const initialValues = props.criteria.jsonSearchParameters && JSON.parse(props.criteria.jsonSearchParameters);
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const { clientSettings } = sessionValue;
    const { initialSearchState, initialFilterState } = PublicPortalState.enforcementNoticeInitialSearchState();
    const initSearchState: EnforcementNoticeSearchState = {
        ...initialSearchState,
        filter: {
            ...initialFilterState,
            ...initialValues,
        },
    };

    const [state, setState] = useState<EnforcementNoticeSearchState>(initSearchState);
    const [searchName, setSearchName] = useState("");
    const [searchNameValidation, setSearchNameValidation] = useState<ValidationResult>({ passed: true, error: "" });

    const handleSearchName = (value: string) => {
        const validationResult = IsStringNotEmptyValidator(value, "Enter Search name");
        setSearchNameValidation(validationResult);
        setSearchName(value);
    };

    useMemo(() => {
        setSearchName(props.criteria.searchName);
        setSearchNameValidation({ passed: true, error: "" });
        setState(initSearchState);
        fetchPicklistOptions();
    }, [props.criteria, props.closeModal]);

    const formActions = <SearchActions onSubmitHandler={handleSave} onCancelHandler={props.closeModal} customSubmitName={"Save"} customCancelName={"Cancel"} />;

    function mapComponent() {
        const initialGeometry = state.filter.geometry ? (JSON.parse(state.filter.geometry) as GeoJSON.GeoJSON) : undefined;
        return (
            <Suspense fallback={<MapFallback />}>
                <MapView
                    geometries={initialGeometry}
                    onChange={(g) => {
                        if (g.drawn) {
                            setStateModifier({ geometry: JSON.stringify(g.drawn.geoJson) });
                        }
                    }}
                    masterLayer={
                        clientSettings
                            ? {
                                  layerUrl: clientSettings.MASTER_LAYER_URL,
                                  attribution: clientSettings.MASTER_LAYER_ATTRIBUTION,
                              }
                            : undefined
                    }
                />
            </Suspense>
        );
    }

    function getForm() {
        switch (initialValues?.searchType) {
            case "Simple": {
                return <EnforcementNoticeSimpleSearch state={state} setter={setStateModifier} />;
            }
            case "Advanced": {
                return <EnforcementNoticeAdvancedSearch state={state} setter={setStateModifier} />;
            }
            case "Map": {
                return mapComponent();
            }
            default:
                return <EnforcementNoticeAdvancedSearch state={state} setter={setStateModifier} />;
        }
    }

    function getBoardStyles() {
        if (initialValues?.searchType === "Map") {
            const mapBoardStyles = {
                width: "75%",
            };

            return mapBoardStyles;
        }
    }

    const boardSize = initialValues?.searchType === "Advanced" ? "big" : "small";

    return (
        <>
            <Modal open={props.isOpen}>
                <Box height={"100%"}>
                    <Board id={boardSize === "big" ? "editSearch__bigBoard" : "editSearch__smallBoard"} size={boardSize} style={getBoardStyles()}>
                        <Box className="editSearch__headerContainer">
                            {UserCollectedDataHelper.getSearchName(searchName, handleSearchName, searchNameValidation)}
                            <IconButton onClick={props.closeModal} className="close-saved-search-modal-button">
                                <FontAwesomeIcon icon={faXmark} style={PublicPortalTheme.icons.sizeM} />
                            </IconButton>
                        </Box>
                        {getForm()}
                        {formActions}
                    </Board>
                </Box>
            </Modal>
        </>
    );

    async function handleSave() {
        if (searchNameValidation.passed) {
            await props.saveEditSearch(props.criteria.id, searchName, props.criteria.searchType, state.filter);
        }
    }

    function setStateModifier(modifier: Partial<EnforcementNoticeFilter>) {
        setState((prev) => {
            return { ...prev, filter: { ...prev.filter, ...modifier } };
        });
    }

    async function fetchPicklistOptions() {
        enfNoticeService.getFilteringLists().then((filter) => {
            setState((prev) => {
                return { ...prev, noticeTypes: filter.NoticeType || [], wards: filter.Wards || [], parishes: filter.Parishes || [] };
            });
        });
    }
};

export default EnforcementNoticeEditSearch;
